import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { documentService } from '../../../services/documentService';
import type {
  DocumentLifecycleScheduleDto,
  UpdateDocumentLifecycleSchedulePayload,
} from '../../../types/documents';

export interface DocumentLifecycleScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  canSchedulePublish: boolean;
  canScheduleArchive: boolean;
  onSuccess: (result: DocumentLifecycleScheduleDto) => void;
}

function utcIsoToLocalDatetimeValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function localDatetimeValueToUtcIso(local: string): string {
  const d = new Date(local);
  return d.toISOString();
}

export const DocumentLifecycleScheduleModal: React.FC<DocumentLifecycleScheduleModalProps> = ({
  isOpen,
  onClose,
  documentId,
  canSchedulePublish,
  canScheduleArchive,
  onSuccess,
}) => {
  const [publishInput, setPublishInput] = useState('');
  const [archiveInput, setArchiveInput] = useState('');
  const [clearPublish, setClearPublish] = useState(false);
  const [clearArchive, setClearArchive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const initialPublish = useRef('');
  const initialArchive = useRef('');

  useEffect(() => {
    if (!isOpen || !documentId) return;
    setClearPublish(false);
    setClearArchive(false);
    setSubmitError('');
    setLoadError('');
    setLoading(true);
    void documentService
      .getDocument(documentId)
      .then(doc => {
        const p = utcIsoToLocalDatetimeValue(doc?.scheduledPublishDate ?? undefined);
        const a = utcIsoToLocalDatetimeValue(doc?.scheduledArchiveDate ?? undefined);
        initialPublish.current = p;
        initialArchive.current = a;
        setPublishInput(p);
        setArchiveInput(a);
      })
      .catch(() => {
        setLoadError('Não foi possível carregar o documento.');
      })
      .finally(() => setLoading(false));
  }, [isOpen, documentId]);

  const handleLimparPublish = () => {
    setPublishInput('');
    setClearPublish(true);
  };

  const handleLimparArchive = () => {
    setArchiveInput('');
    setClearArchive(true);
  };

  const handleGuardar = async () => {
    setSubmitError('');
    const body: UpdateDocumentLifecycleSchedulePayload = {};

    if (canSchedulePublish) {
      const publishChanged =
        clearPublish || publishInput.trim() !== (initialPublish.current || '').trim();
      if (publishChanged) {
        body.patchScheduledPublish = true;
        if (clearPublish) {
          body.clearScheduledPublish = true;
        } else if (publishInput.trim()) {
          body.scheduledPublishUtc = localDatetimeValueToUtcIso(publishInput.trim());
        } else {
          setSubmitError('Defina uma data de publicação ou use Limpar.');
          return;
        }
      }
    }

    if (canScheduleArchive) {
      const archiveChanged =
        clearArchive || archiveInput.trim() !== (initialArchive.current || '').trim();
      if (archiveChanged) {
        body.patchScheduledArchive = true;
        if (clearArchive) {
          body.clearScheduledArchive = true;
        } else if (archiveInput.trim()) {
          body.scheduledArchiveUtc = localDatetimeValueToUtcIso(archiveInput.trim());
        } else {
          setSubmitError('Defina uma data de arquivamento ou use Limpar.');
          return;
        }
      }
    }

    if (!body.patchScheduledPublish && !body.patchScheduledArchive) {
      setSubmitError('Nenhuma alteração a guardar.');
      return;
    }

    setLoading(true);
    try {
      const result = await documentService.updateDocumentLifecycleSchedule(documentId, body);
      onClose();
      onSuccess(result);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const msg =
        err?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Não foi possível guardar o agendamento.');
      setSubmitError(typeof msg === 'string' ? msg : 'Não foi possível guardar o agendamento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !loading && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  Agendar publicação e arquivamento
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-600">
                  As datas são guardadas em UTC e validadas com o mesmo motor de workflow que as ações
                  manuais. O arquivamento automático só é aceite quando o documento já está publicado.
                </p>
                {loadError ? (
                  <p className="mt-3 text-sm text-red-600">{loadError}</p>
                ) : (
                  <div className="mt-4 space-y-5">
                    {canSchedulePublish && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Publicação automática (hora local)
                        </label>
                        <input
                          type="datetime-local"
                          value={publishInput}
                          onChange={e => {
                            setPublishInput(e.target.value);
                            setClearPublish(false);
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={handleLimparPublish}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                          disabled={loading}
                        >
                          Limpar publicação agendada
                        </button>
                      </div>
                    )}
                    {canScheduleArchive && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Arquivamento automático (hora local)
                        </label>
                        <input
                          type="datetime-local"
                          value={archiveInput}
                          onChange={e => {
                            setArchiveInput(e.target.value);
                            setClearArchive(false);
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={handleLimparArchive}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                          disabled={loading}
                        >
                          Limpar arquivo agendado
                        </button>
                      </div>
                    )}
                    {!canSchedulePublish && !canScheduleArchive && (
                      <p className="text-sm text-gray-500">Não tem permissão para agendar neste documento.</p>
                    )}
                  </div>
                )}
                {submitError ? <p className="mt-3 text-sm text-red-600">{submitError}</p> : null}
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => !loading && onClose()}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    disabled={loading}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleGuardar()}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading || !!loadError || (!canSchedulePublish && !canScheduleArchive)}
                  >
                    {loading ? 'A guardar…' : 'Guardar'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
